/**
 * Asks before `/konto` is left with unsaved work (owner, 4 November 2026). Whether there IS work is
 * `model/unsavedWork`'s answer; this is the wiring: the router's blocker for a move inside the app,
 * and `beforeunload` for a reload or a closed tab, which the browser words itself.
 *
 * Only a move to ANOTHER path is held. A tab switch changes `?tab=` on `/konto` and keeps every
 * draft, so asking there would be a question about nothing. The prompt is the app's own
 * `ConfirmDialog`, as `/rezerwacje`'s week draft asks the same question.
 *
 * An exit the trainer chose (signing out, the landing after an erased account, the payment page,
 * Google's consent page) goes through `shared/lib/documentExit` and is never held: both answers below
 * are read at the moment of the move, and the installed `@tanstack/history` calls a function-valued
 * `enableBeforeUnload` inside its `beforeunload` listener. A `true` there answered the app's own
 * `location.assign` too, and „Stay" left a signed-out trainer on `/konto`.
 */

import { useBlocker } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { isLeavingDocument } from '~/shared/lib/documentExit';

import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

export interface LeaveGuardApi {
  /** The question, while a move is being held; `null` otherwise. */
  readonly prompt: ConfirmPrompt | null;
  readonly onLeave: () => void;
  readonly onStay: () => void;
}

export function useLeaveGuard(unsaved: boolean): LeaveGuardApi {
  const { t } = useTranslation();
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      !isLeavingDocument() && current.pathname !== next.pathname,
    enableBeforeUnload: () => !isLeavingDocument(),
    disabled: !unsaved,
    withResolver: true,
  });

  return {
    prompt:
      blocker.status === 'blocked'
        ? {
            title: t('web:account.leave.title'),
            description: t('web:account.leave.body'),
            confirmLabel: t('web:account.leave.confirm'),
            cancelLabel: t('web:account.leave.cancel'),
            destructiveConfirm: true,
          }
        : null,
    onLeave: () => {
      if (blocker.status === 'blocked') blocker.proceed();
    },
    onStay: () => {
      if (blocker.status === 'blocked') blocker.reset();
    },
  };
}
